import type { PlanDefinition, PlanDefinitionAction } from "@medplum/fhirtypes";
import { FLOW_CONDITION_LANGUAGE, FLOW_DEFINITION_KIND, getTRPCClient } from "@repo/api";
import type {
  ChangeFlowDefinitionAvailabilityInput,
  DefineFlowDefinitionInput,
  ReviseFlowDefinitionInput,
} from "@repo/api/schemas";
import type { QuestionnaireFactCondition } from "@repo/domain";
import { mintEntityId } from "@repo/domain";
import { canonicalBase, canonicalUrl } from "@repo/fhir-systems";
import { and, eq, max, useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import { withFactConditions } from "../fhir/patient-fact-condition-storage";
import type { QuestionnaireCondition } from "../fhir/questionnaire-definition-storage";
import type { PlanDefinitionRow } from "../fhir/types";

const COLLECTION = "planDefinition";
export const FLOW_DEFINITION_CANONICAL_BASE = canonicalBase("PlanDefinition/flow");

/** Every node is a questionnaire; one with no outgoing edge is where a branch ends. */
export interface FlowNodeDto {
  id: string;
  questionnaire: string;
}

/**
 * A single comparison (shared with form enablement), "is any of / none of" a
 * choice question's options, or a comparison against one of the form's scores.
 */
export type FlowConditionDto =
  | QuestionnaireFactCondition
  | QuestionnaireCondition
  | { question: string; operator: "in" | "not in"; value: { kind: "Choices"; optionIds: string[] } }
  | FlowScoreConditionDto;

export interface FlowScoreConditionDto {
  score: string;
  operator: "=" | "!=" | ">=" | "<=" | ">" | "<";
  value: number;
}

export interface FlowEdgeDto {
  id: string;
  from: string;
  to: string;
  conditions: readonly FlowConditionDto[];
  conditionBehavior?: "any" | "all";
}

export interface FlowDefinitionDto {
  url: string;
  version: number;
  mintedAt?: string;
  name: string;
  description?: string;
  availability: "Active" | "Inactive";
  nodes: readonly FlowNodeDto[];
  /** Every matching rule out of a node fires; a rule with no conditions is the node's default. */
  edges: readonly FlowEdgeDto[];
}

export function mintFlowDefinitionUrl(): string {
  return canonicalUrl("PlanDefinition/flow", mintEntityId());
}

function nodeOf(action: PlanDefinitionAction): FlowNodeDto {
  return { id: action.id ?? "", questionnaire: action.definitionCanonical ?? "" };
}

function edgeOf(from: string, action: PlanDefinitionAction): FlowEdgeDto {
  const expression = action.condition?.find((c) => c.expression?.language === FLOW_CONDITION_LANGUAGE)?.expression
    ?.expression;
  const stored = expression
    ? (JSON.parse(expression) as Pick<FlowEdgeDto, "conditions" | "conditionBehavior">)
    : { conditions: [] };
  return {
    id: action.id ?? "",
    from,
    to: action.relatedAction?.find((related) => related.relationship === "before-start")?.actionId ?? "",
    ...stored,
    conditions: withFactConditions(stored.conditions, action.extension),
  };
}

/** Pure row → DTO. A spike read; a real one would live beside a storage validator (ADR-0021). */
export function projectFlowDefinition(resource: PlanDefinition): FlowDefinitionDto {
  const actions = resource.action ?? [];
  return {
    url: resource.url ?? "",
    version: Number(resource.version),
    ...(resource.date ? { mintedAt: resource.date } : {}),
    name: resource.title ?? "",
    ...(resource.description ? { description: resource.description } : {}),
    availability: resource.status === "active" ? "Active" : "Inactive",
    nodes: actions.map(nodeOf),
    edges: actions.flatMap((action) => (action.action ?? []).map((child) => edgeOf(action.id ?? "", child))),
  };
}

function useLatestFlowRows(url?: string) {
  const collection = useCollection(COLLECTION);
  return useLiveQuery({
    queryKey: [collection.id, "flow-definitions", url ?? null],
    query: (query) => {
      const latest = query
        .from({ candidate: collection })
        .where(({ candidate }) => eq(candidate.catalogModelKind, FLOW_DEFINITION_KIND))
        .groupBy(({ candidate }) => candidate.url)
        .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
      let current = query
        .from({ definition: collection })
        .join({ latest }, ({ definition, latest }) => eq(definition.url, latest.url))
        .where(({ definition, latest }) =>
          and(eq(definition.catalogModelKind, FLOW_DEFINITION_KIND), eq(definition.catalogVersion, latest.version)),
        );
      if (url) current = current.where(({ definition }) => eq(definition.url, url));
      return current
        .orderBy(({ definition }) => definition.title, "asc")
        .fn.select(({ definition }) => projectFlowDefinition(definition as PlanDefinitionRow));
    },
  });
}

export function useFlowDefinitions(): FlowDefinitionDto[] {
  return (useLatestFlowRows().data ?? []) as FlowDefinitionDto[];
}

export function useFlowDefinition(url: string | undefined): FlowDefinitionDto | undefined {
  const rows = (useLatestFlowRows(url ?? "__none__").data ?? []) as FlowDefinitionDto[];
  return url ? rows[0] : undefined;
}

export async function defineFlowDefinition(input: DefineFlowDefinitionInput): Promise<void> {
  await getTRPCClient().catalog.flowDefinition.define.mutate(input);
}

export async function reviseFlowDefinition(input: ReviseFlowDefinitionInput): Promise<void> {
  await getTRPCClient().catalog.flowDefinition.revise.mutate(input);
}

export async function activateFlowDefinition(input: ChangeFlowDefinitionAvailabilityInput): Promise<void> {
  await getTRPCClient().catalog.flowDefinition.activate.mutate(input);
}

export async function deactivateFlowDefinition(input: ChangeFlowDefinitionAvailabilityInput): Promise<void> {
  await getTRPCClient().catalog.flowDefinition.deactivate.mutate(input);
}
